/**
 * #1853 — a minimal translation layer for the ticketbox (no library).
 *
 * Scope: the group-claim flow and what surrounds it on screen — the left rail,
 * /orders (OrderLookupPage + GroupClaimPanel + the reprint dialog) and
 * /setup (DeviceSetupPage). Everything else stays Indonesian.
 *
 * Indonesian is the default and is the text these screens rendered before
 * this layer existed, character for character. Server data (product names,
 * booking names, invoice numbers) is never translated.
 */
import { useLocaleStore } from '@/stores/locale.store';

export type Locale = 'id' | 'en';
export const LOCALES: readonly Locale[] = ['id', 'en'];

const id = {
  // ── Left rail ─────────────────────────────────────────────────────────────
  'nav.home': 'Beranda',
  'nav.sell': 'Jual',
  'nav.foc': 'FOC',
  'nav.orders': 'Pesanan',
  'nav.print': 'Cetak',
  'nav.addon': 'Add-on',
  'nav.shift': 'Shift',
  'nav.requests': 'Ajuan',
  'rail.logout': 'Keluar',
  'rail.cashier': 'Kasir',
  'rail.printerBound': 'Printer terhubung',
  'rail.printerUnbound': 'Printer belum diatur',
  'rail.readerBound': 'Pembaca kartu terhubung',
  'rail.readerUnbound': 'Pembaca kartu belum diatur',
  'lang.label': 'Bahasa',

  // ── Order lookup (/orders) ────────────────────────────────────────────────
  'orders.title': 'Cari Pesanan',
  'orders.searchPlaceholder': 'Nomor invoice / GRP-… atau scan barcode…',
  'orders.clearSearch': 'Bersihkan pencarian',
  'orders.search': 'Cari',
  'orders.show': 'Tampilkan',
  'orders.scopeOwn': 'Milik saya',
  'orders.scopeAll': 'Semua kasir',
  'orders.date': 'Tanggal',
  'orders.allDates': 'Semua tanggal',
  'orders.clearFilter': 'Hapus filter',
  'orders.count': '{count} pesanan',
  'orders.countOne': '{count} pesanan',
  'orders.notFound': 'Tidak ditemukan',
  'orders.notFoundDesc': 'Tidak ada pesanan untuk "{q}".',
  'orders.empty': 'Belum ada pesanan',
  'orders.emptyDate': 'Tidak ada transaksi pada tanggal ini. Ganti tanggal atau lihat semua tanggal.',
  'orders.emptyAll': 'Belum ada transaksi ticketbox.',
  'orders.emptyOwn': 'Belum ada transaksi pada akun ini.',
  'orders.partnerRow': '{bookings} · kunjungan {date} · {headcount} orang',
  'orders.pendingApproval': 'Menunggu persetujuan',
  'orders.prev': 'Sebelumnya',
  'orders.next': 'Berikutnya',
  'orders.pageOf': 'Halaman {page} dari {total}',
  'orders.detail': 'Detail',
  'orders.noneSelected': 'Belum ada pesanan dipilih',
  'orders.noneSelectedDesc': 'Pilih pesanan dari hasil pencarian untuk melihat detail dan mencetak ulang.',
  'orders.resumePayment': 'Lanjutkan Pembayaran',
  'orders.reprintTickets': 'Cetak Ulang Tiket',
  'orders.printUnprinted': 'Cetak {count} Tiket Belum Dicetak',
  'orders.printTickets': 'Cetak Tiket',
  'orders.printReceipt': 'Cetak Struk',
  'orders.groupReprintTitle': 'Cetak ulang dengan QR baru',
  'orders.groupPrintTitle': 'Cetak',
  'orders.awaitingSupervisor': 'Menunggu persetujuan supervisor',
  'orders.requestVoid': 'Ajukan Void',
  'orders.requestRefund': 'Ajukan Refund',
  'orders.toast.printerNotSet': 'Printer belum diatur — tidak dapat mencetak.',
  'orders.toast.cancelledNoPrint': 'Tiket dari transaksi yang dibatalkan/refund tidak dapat dicetak.',
  'orders.toast.groupNotClaimable': 'Rombongan ini tidak dapat diambil sekarang.',
  'orders.toast.printedSomeFailed': '{ok} tiket dicetak, {failed} gagal.',
  'orders.toast.printed': '{ok} tiket dicetak.',
  // Nothing printed AND nothing refused — the invoice had no printable band.
  'orders.toast.printedNone': 'Tidak ada tiket yang dicetak.',
  'orders.toast.reprintedSomeFailed': '{ok} dicetak ulang, {failed} gagal. {message}',
  'orders.toast.reprinted': '{ok} gelang dicetak ulang dengan QR baru.',
  'orders.toast.reprintedGroup': '{label}: {ok} gelang dicetak ulang dengan QR baru.',
  'orders.toast.groupPrinted': '{label} dicetak ({count}).',
  'orders.toast.paymentReceived': 'Pembayaran diterima — tiket siap dicetak.',
  'printStatus.PENDING': 'Belum dicetak',
  'printStatus.PRINTING': 'Sebagian dicetak',
  'printStatus.PRINTED': 'Tercetak',
  'printStatus.FAILED': 'Gagal cetak',
  'channel.affiliator': 'Affiliator',
  'channel.affiliator_hotel': 'Affiliator · Hotel',
  'approvalKind.VOID': 'Void',
  'approvalKind.FULL_REFUND': 'Refund penuh',
  'approvalKind.PARTIAL_REFUND': 'Refund sebagian',

  // ── Sale status pill ──────────────────────────────────────────────────────
  'status.SUCCESS': 'Berhasil',
  'status.SETTLEMENT': 'Lunas',
  'status.PENDING': 'Menunggu',
  'status.INITIAL': 'Awal',
  'status.FAILED': 'Gagal',
  'status.CANCELLED': 'Dibatalkan',
  'status.REFUND': 'Refund',

  // ── Reprint reason dialog ─────────────────────────────────────────────────
  'reprint.title': 'Cetak ulang gelang',
  'reprint.titleMany': 'Cetak ulang gelang ({count})',
  'reprint.description': 'Pilih alasan cetak ulang.',
  'reprint.warn1': 'Gelang baru memakai ',
  'reprint.warn2': 'QR baru',
  'reprint.warn3': '. Gelang lama dan e-tiket pengunjung',
  'reprint.warn4': 'tidak berlaku lagi',
  'reprint.warn5': ' di gerbang.',
  'reprint.reasonsLabel': 'Alasan cetak ulang',
  'reprint.reason.DAMAGED': 'Gelang rusak / tidak terbaca',
  'reprint.reason.LOST': 'Gelang hilang',
  'reprint.reason.PRINTER_JAM': 'Printer macet / hasil cetak gagal',
  'reprint.reason.OTHER': 'Lainnya',
  'reprint.note': 'Catatan',
  'reprint.noteOptional': ' (opsional)',
  'reprint.notePlaceholder': 'Wajib diisi untuk alasan lainnya',
  'reprint.submit': 'Cetak ulang dengan QR baru',

  // ── Group claim (travel agent / OTA / hotel guest groups) ─────────────────
  'claim.heading.agent': 'Rombongan Travel Agent',
  'claim.heading.ota': 'OTA',
  'claim.heading.affiliator': 'Affiliator',
  'claim.heading.hotel': 'Affiliator · Hotel',
  'claim.visitLine': 'Kunjungan {date} · {count} gelang',
  'claim.people': '{count} orang',
  'claim.notClaimed': 'Belum diambil',
  'claim.allAtOnce': 'Rombongan diambil sekaligus: semua {count} gelang dicetak bersama.',
  'claim.button.printAll': 'Cetak Semua Tiket',
  'claim.button.claimed': 'Sudah diambil',
  'claim.button.claim': 'Ambil & cetak {count} gelang',
  // The claim line and refusals come from the server in Indonesian
  // (GroupClaimService); Indonesian shows the server's text as-is and these
  // mirror it so both dictionaries stay key-identical.
  'claim.line': 'Diklaim {time} di {where}, {claimed} dari {booked}',
  'claim.lineStaff': 'Diklaim {time} di {where} oleh {name}, {claimed} dari {booked}',
  'claim.where.TICKETBOX': 'ticketbox',
  'claim.where.KIOSK': 'kiosk',
  'claim.where.BACKOFFICE': 'back office',
  'claim.refusal.alreadyClaimed': 'Gelang rombongan ini sudah diambil — {line}.',
  'claim.refusal.cancelled': 'Pemesanan {booking} dibatalkan — gelang tidak dapat dicetak.',
  'claim.refusal.cancelledAny': 'Pemesanan ini dibatalkan — gelang tidak dapat dicetak.',
  'claim.refusal.unpaidGuest': 'Pemesanan {booking} belum dibayar — minta tamu menghubungi hotel.',
  'claim.refusal.unpaidAgent': 'Pemesanan {booking} belum dibayar — arahkan ke meja grup.',
  'claim.refusal.wrongDate': 'Pemesanan {booking} untuk tanggal {day}, bukan hari ini ({today}).',

  // ── Device setup (/setup) ─────────────────────────────────────────────────
  'device.title': 'Atur Perangkat Loket',
  'device.subtitle': 'Ikat printer tiket, pembaca kartu, dan terminal EDC ke loket ini.',
  'device.noPrinters': 'Tidak ada printer tersedia',
  'device.noPrintersDesc':
    'Belum ada printer tiket yang terdaftar. Hubungi administrator untuk menambahkan perangkat sebelum berjualan.',
  'device.identity': 'Identitas loket',
  'device.identityDesc': 'Nama untuk mengenali terminal ini di laporan.',
  'device.name': 'Nama loket',
  'device.namePlaceholder': 'Loket A1',
  'device.printer': 'Printer tiket',
  'device.printerDesc': 'Wajib — tiket dicetak melalui printer ini.',
  'device.printerPick': 'Pilih printer',
  'device.reader': 'Pembaca kartu (e-money)',
  'device.readerDesc': 'Opsional — hanya untuk pembayaran e-money.',
  'device.readerPick': 'Pilih pembaca kartu',
  'device.none': 'Tidak ada',
  'device.readerDefault': 'Default dari konfigurasi: MID {mid} · TID {tid}.',
  'device.edc': 'Terminal kartu (EDC Permata)',
  'device.edcDesc': 'Opsional — hanya untuk pembayaran kartu debit/kredit.',
  'device.edcPick': 'Pilih terminal kartu',
  'device.edcNote': 'Tanpa terminal, pembayaran kartu tidak bisa dipilih di kasir ini.',
  'device.save': 'Simpan & Mulai Berjualan',
  'device.toast.pickPrinter': 'Pilih printer tiket terlebih dahulu — wajib untuk mencetak tiket.',
  'device.toast.bound': 'Perangkat terikat. Selamat bekerja!',
  'device.toast.saveFailed': 'Gagal menyimpan perangkat.',
} as const;

export type MessageKey = keyof typeof id;

const en: Record<MessageKey, string> = {
  'nav.home': 'Home',
  'nav.sell': 'Sell',
  'nav.foc': 'FOC',
  'nav.orders': 'Orders',
  'nav.print': 'Print',
  'nav.addon': 'Add-on',
  'nav.shift': 'Shift',
  'nav.requests': 'Requests',
  'rail.logout': 'Sign out',
  'rail.cashier': 'Cashier',
  'rail.printerBound': 'Printer connected',
  'rail.printerUnbound': 'Printer not set up',
  'rail.readerBound': 'Card reader connected',
  'rail.readerUnbound': 'Card reader not set up',
  'lang.label': 'Language',

  'orders.title': 'Find order',
  'orders.searchPlaceholder': 'Invoice number / GRP-… or scan barcode…',
  'orders.clearSearch': 'Clear search',
  'orders.search': 'Search',
  'orders.show': 'Show',
  'orders.scopeOwn': 'Mine',
  'orders.scopeAll': 'All cashiers',
  'orders.date': 'Date',
  'orders.allDates': 'All dates',
  'orders.clearFilter': 'Clear filter',
  'orders.count': '{count} orders',
  'orders.countOne': '{count} order',
  'orders.notFound': 'Not found',
  'orders.notFoundDesc': 'No orders for "{q}".',
  'orders.empty': 'No orders yet',
  'orders.emptyDate': 'No transactions on this date. Pick another date or show all dates.',
  'orders.emptyAll': 'No ticketbox transactions yet.',
  'orders.emptyOwn': 'No transactions on this account yet.',
  'orders.partnerRow': '{bookings} · visit {date} · {headcount} people',
  'orders.pendingApproval': 'Awaiting approval',
  'orders.prev': 'Previous',
  'orders.next': 'Next',
  'orders.pageOf': 'Page {page} of {total}',
  'orders.detail': 'Details',
  'orders.noneSelected': 'No order selected',
  'orders.noneSelectedDesc': 'Pick an order from the results to see its details and reprint.',
  'orders.resumePayment': 'Continue payment',
  'orders.reprintTickets': 'Reprint tickets',
  'orders.printUnprinted': 'Print {count} unprinted tickets',
  'orders.printTickets': 'Print tickets',
  'orders.printReceipt': 'Print receipt',
  'orders.groupReprintTitle': 'Reprint with a new QR',
  'orders.groupPrintTitle': 'Print',
  'orders.awaitingSupervisor': 'Awaiting supervisor approval',
  'orders.requestVoid': 'Request void',
  'orders.requestRefund': 'Request refund',
  'orders.toast.printerNotSet': 'Printer not set up — cannot print.',
  'orders.toast.cancelledNoPrint': 'Tickets from a cancelled or refunded transaction cannot be printed.',
  'orders.toast.groupNotClaimable': 'This group cannot be claimed right now.',
  'orders.toast.printedSomeFailed': '{ok} tickets printed, {failed} failed.',
  'orders.toast.printed': '{ok} tickets printed.',
  'orders.toast.printedNone': 'No tickets were printed.',
  'orders.toast.reprintedSomeFailed': '{ok} reprinted, {failed} failed. {message}',
  'orders.toast.reprinted': '{ok} wristbands reprinted with a new QR.',
  'orders.toast.reprintedGroup': '{label}: {ok} wristbands reprinted with a new QR.',
  'orders.toast.groupPrinted': '{label} printed ({count}).',
  'orders.toast.paymentReceived': 'Payment received — tickets ready to print.',
  'printStatus.PENDING': 'Not printed yet',
  'printStatus.PRINTING': 'Partly printed',
  'printStatus.PRINTED': 'Printed',
  'printStatus.FAILED': 'Print failed',
  'channel.affiliator': 'Affiliate',
  'channel.affiliator_hotel': 'Affiliate · Hotel',
  'approvalKind.VOID': 'Void',
  'approvalKind.FULL_REFUND': 'Full refund',
  'approvalKind.PARTIAL_REFUND': 'Partial refund',

  'status.SUCCESS': 'Success',
  'status.SETTLEMENT': 'Settled',
  'status.PENDING': 'Pending',
  'status.INITIAL': 'Initial',
  'status.FAILED': 'Failed',
  'status.CANCELLED': 'Cancelled',
  'status.REFUND': 'Refund',

  'reprint.title': 'Reprint wristband',
  'reprint.titleMany': 'Reprint wristbands ({count})',
  'reprint.description': 'Choose why you are reprinting.',
  'reprint.warn1': 'The new wristband gets a ',
  'reprint.warn2': 'new QR',
  'reprint.warn3': ". The old wristband and the visitor's e-ticket",
  'reprint.warn4': 'stop working',
  'reprint.warn5': ' at the gates.',
  'reprint.reasonsLabel': 'Reprint reason',
  'reprint.reason.DAMAGED': 'Wristband damaged / will not scan',
  'reprint.reason.LOST': 'Wristband lost',
  'reprint.reason.PRINTER_JAM': 'Printer jammed / bad print',
  'reprint.reason.OTHER': 'Other',
  'reprint.note': 'Note',
  'reprint.noteOptional': ' (optional)',
  'reprint.notePlaceholder': 'Required for the reason Other',
  'reprint.submit': 'Reprint with a new QR',

  'claim.heading.agent': 'Travel agent group',
  'claim.heading.ota': 'OTA group',
  'claim.heading.affiliator': 'Affiliate group',
  'claim.heading.hotel': 'Hotel guest group',
  'claim.visitLine': 'Visit {date} · {count} wristbands',
  'claim.people': '{count} people',
  'claim.notClaimed': 'Not claimed yet',
  'claim.allAtOnce': 'The group is claimed all at once: all {count} wristbands print together.',
  'claim.button.printAll': 'Print all tickets',
  'claim.button.claimed': 'Already claimed',
  'claim.button.claim': 'Claim & print {count} wristbands',
  'claim.line': 'Claimed {time} at {where}, {claimed} of {booked}',
  'claim.lineStaff': 'Claimed {time} at {where} by {name}, {claimed} of {booked}',
  'claim.where.TICKETBOX': 'ticketbox',
  'claim.where.KIOSK': 'kiosk',
  'claim.where.BACKOFFICE': 'back office',
  'claim.refusal.alreadyClaimed': "This group's wristbands were already claimed — {line}.",
  'claim.refusal.cancelled': 'Booking {booking} is cancelled — wristbands cannot be printed.',
  'claim.refusal.cancelledAny': 'This booking is cancelled — wristbands cannot be printed.',
  'claim.refusal.unpaidGuest': 'Booking {booking} is not paid yet — ask the guest to contact the hotel.',
  'claim.refusal.unpaidAgent': 'Booking {booking} is not paid yet — send them to the group desk.',
  'claim.refusal.wrongDate': 'Booking {booking} is for {day}, not today ({today}).',

  'device.title': 'Set up ticketbox devices',
  'device.subtitle': 'Link the ticket printer, card reader and EDC terminal to this ticketbox.',
  'device.noPrinters': 'No printers available',
  'device.noPrintersDesc':
    'No ticket printer is registered yet. Ask an administrator to add one before you start selling.',
  'device.identity': 'Ticketbox identity',
  'device.identityDesc': 'The name that identifies this terminal in reports.',
  'device.name': 'Ticketbox name',
  'device.namePlaceholder': 'Ticketbox A1',
  'device.printer': 'Ticket printer',
  'device.printerDesc': 'Required — tickets print on this printer.',
  'device.printerPick': 'Choose a printer',
  'device.reader': 'Card reader (e-money)',
  'device.readerDesc': 'Optional — only for e-money payments.',
  'device.readerPick': 'Choose a card reader',
  'device.none': 'None',
  'device.readerDefault': 'Default from config: MID {mid} · TID {tid}.',
  'device.edc': 'Card terminal (EDC Permata)',
  'device.edcDesc': 'Optional — only for debit/credit card payments.',
  'device.edcPick': 'Choose a card terminal',
  'device.edcNote': 'Without a terminal, card payment cannot be chosen at this till.',
  'device.save': 'Save & start selling',
  'device.toast.pickPrinter': 'Choose a ticket printer first — it is required to print tickets.',
  'device.toast.bound': 'Devices linked. Have a good shift!',
  'device.toast.saveFailed': 'Could not save the devices.',
};

export const dictionaries: Record<Locale, Record<MessageKey, string>> = { id, en };

export type TranslateVars = Record<string, string | number | null | undefined>;

/** The language currently chosen on this terminal (default `id`). */
export function getLocale(): Locale {
  return useLocaleStore.getState().locale;
}

/** Is `key` one of ours? For maps keyed by server values (status, channel…). */
export function hasKey(key: string): key is MessageKey {
  return Object.prototype.hasOwnProperty.call(id, key);
}

/**
 * Translate `key`, replacing `{name}` with `vars.name`. Reads the current
 * locale unless one is passed. A missing key falls back to Indonesian, then
 * to the key itself.
 */
export function t(key: MessageKey, vars?: TranslateVars, locale: Locale = getLocale()): string {
  const template = dictionaries[locale]?.[key] ?? id[key] ?? key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const v = vars[name];
    return v === undefined || v === null ? match : String(v);
  });
}

export type Translate = (key: MessageKey, vars?: TranslateVars) => string;

/** React hook: re-renders on a language switch; returns `{ locale, t }`. */
export function useT(): { locale: Locale; t: Translate } {
  const locale = useLocaleStore((s) => s.locale);
  return { locale, t: (key, vars) => t(key, vars, locale) };
}
